import { useEffect, useState } from 'react'
import { ApiError, api, type Filter, type RunPreview } from '../api'
import { ClauseBuilder } from '../ClauseBuilder'
import { useNotice } from '../useNotice'
import { useRefreshOnActivate } from '../useActive'

/**
 * Filters — the named audiences campaigns are pointed at.
 *
 * A filter is a condition and nothing more (D84, D85): no priority, no prompt,
 * no cooldown. Those live on the campaign. Filters are non-exclusive — an
 * account may match several — so the table is alphabetical and there is no
 * order to protect.
 *
 * The detail pane leads with the condition and the notes, because the notes
 * are where the reasoning lives — several record
 * defects that were already found and fixed, and reading them is what stops
 * someone reintroducing one.
 */
export function FiltersTab({
  active = true,
  selected: selectedProp,
  onSelect,
  onDeleted,
  onRunFilter,
}: {
  active?: boolean
  selected?: string | null
  onSelect?: (name: string) => void
  onDeleted?: () => void
  /** Hand this filter to stage 2. Absent when the tab is rendered alone. */
  onRunFilter?: (name: string) => void
}) {
  const [filters, setFilters] = useState<Filter[] | null>(null)
  const [selected, setSelected] = useState<string | null>(selectedProp ?? null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!active) return
    setSelected(selectedProp ?? null)
  }, [active, selectedProp])

  const load = async () => {
    try {
      setError(null)
      setFilters(await api.filters())
    } catch (err) {
      setError(String((err as Error).message))
    }
  }

  useEffect(() => {
    void load()
  }, [])

  useRefreshOnActivate(active, load)

  if (error) return <div className="error">{error}</div>
  if (!filters) return <div className="loading">Loading filters…</div>

  const current = filters.find((f) => f.name === selected) ?? null

  return (
    <div className="split">
      <div>
      <NewFilter
        existing={filters}
        onCreated={(name) => {
          void load()
          if (onSelect) onSelect(name)
          else setSelected(name)
        }}
      />
      <div className="panel">
        <h2>
          Filters <span className="count">{filters.length}</span>
        </h2>
        <p className="hint panel-note">
          Named audiences. There is no order: an account may match several filters, and each
          campaign asks only whether an account matches <em>its</em> filter. A{' '}
          <span className="badge parked">parked</span> filter still matches — that keeps the
          population countable — but cannot back an enabled campaign.
        </p>
        <table>
          <thead>
            <tr>
              <th>Name</th>
              <th>Audience</th>
              <th className="num">Campaigns</th>
              <th>Copy</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {filters.map((f) => (
              <tr
                key={f.name}
                className={`clickable${f.name === selected ? ' selected' : ''}`}
                onClick={() => {
                  if (onSelect) onSelect(f.name)
                  else setSelected(f.name)
                }}
              >
                <td className="mono">{f.name}</td>
                <td>{f.title}</td>
                <td className="num">
                  {f.campaigns ?? 0}
                  {f.enabled_campaigns ? <span className="dim"> ({f.enabled_campaigns} enabled)</span> : null}
                </td>
                <td><CopyState filter={f} /></td>
                <td>
                  {f.parked && <span className="badge parked">parked</span>}{' '}
                  {f.unconditional && <span className="badge catchall">no condition</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      </div>

      {current ? (
        <FilterDetail
          key={current.name}
          filter={current}
          onRunFilter={onRunFilter}
          onChanged={load}
          onDeleted={() => {
            if (onDeleted) onDeleted()
            else setSelected(null)
            void load()
          }}
        />
      ) : (
        <div className="panel">
          <h2>Detail</h2>
          <div className="empty">Select a filter.</div>
        </div>
      )}
    </div>
  )
}

/**
 * Whether this segment can write an email of its own.
 *
 * Composition NEVER FAILS — every slot ends in an unconditional fallback — so a
 * segment with no approved copy does not error, it quietly produces six generic
 * sentences that would suit anybody. Two of the ten sendable segments are in
 * that state today, both with copy already written and waiting on approval, and
 * nothing on any screen said so.
 */
function CopyState({ filter }: { filter: Filter }) {
  const own = filter.slots_with_own_copy
  const waiting = filter.awaiting_approval ?? 0
  if (own === undefined) return <span className="dim">—</span>
  if (own === 0) {
    return (
      <span
        className="badge bad"
        title={
          `No approved fragment names this segment, so every slot falls back to ` +
          `generic copy and a campaign here would send six sentences that suit ` +
          `anybody.` +
          (waiting ? ` ${waiting} fragment(s) are written and awaiting approval.` : '')
        }
      >
        none{waiting ? ` · ${waiting} waiting` : ''}
      </span>
    )
  }
  return (
    <span
      className={own >= 5 ? 'badge ok' : 'badge parked'}
      title={
        `${own} of 6 slots have approved copy naming this segment; the rest fall ` +
        `back to generic sentences.` +
        (waiting ? ` ${waiting} more written and awaiting approval.` : '')
      }
    >
      {own}/6{waiting ? ` · ${waiting} waiting` : ''}
    </span>
  )
}


/**
 * `Free, Quota Pressure` -> `free-quota-pressure`.
 *
 * Offered rather than applied. Rewriting what somebody is typing under their
 * cursor is worse than telling them — and the identifier is permanent, so the
 * one thing this must not do is quietly decide it for them.
 */
export function slugify(raw: string): string {
  return raw
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
}

/**
 * What is wrong with this identifier, in the order it matters.
 *
 * ONE PROBLEM AT A TIME, blocking first. A list of five complaints about a
 * half-typed word is noise, and the field is being typed into as this
 * renders. Null while there is nothing useful to say.
 */
export function slugProblem(
  name: string,
  existing: Filter[],
): { level: 'bad' | 'warn'; message: string; suggestion?: string } | null {
  const raw = name.trim()
  if (!raw) return null

  // Taken is first because no amount of tidying fixes it, and the API would
  // otherwise refuse with a 409 only after Create was pressed.
  if (existing.some((f) => f.name === raw)) {
    return {
      level: 'bad',
      message: `A filter called ${raw} already exists. Identifiers are unique.`,
    }
  }

  const tidy = slugify(raw)
  if (tidy !== raw) {
    // Naming the specific offence is more use than "invalid": people fix what
    // they can see.
    const faults: string[] = []
    if (/\s/.test(raw)) faults.push('spaces')
    if (/[A-Z]/.test(raw)) faults.push('capitals')
    if (/[^A-Za-z0-9\s-]/.test(raw)) faults.push('punctuation')
    if (/^-|-$/.test(raw)) faults.push('a leading or trailing hyphen')
    const what = faults.length
      ? faults.join(', ')
      : 'characters that do not belong in a slug'
    return {
      level: 'warn',
      message: `This has ${what}. It goes in a URL and never changes, so it is worth tidying.`,
      suggestion: tidy || undefined,
    }
  }

  // Shape is fine. What is left is about it being a name somebody reads in six
  // months — a judgement rather than a rule, so it never blocks.
  if (raw.length < 4) {
    return {
      level: 'warn',
      message:
        'Very short. This is the permanent name for the segment, not a placeholder.',
    }
  }
  return null
}

/**
 * The identifier's problems, said before Create rather than after.
 *
 * Not the enforcement — the API refuses a duplicate with a 409 and Postgres
 * refuses a clash regardless. This is the difference between finding out now
 * and finding out after pressing a button whose result cannot be renamed.
 */
function SlugCheck({
  name,
  existing,
  onUse,
}: {
  name: string
  existing: Filter[]
  onUse: (slug: string) => void
}) {
  const problem = slugProblem(name, existing)
  if (!problem) return null
  return (
    <div className={problem.level === 'bad' ? 'error' : 'notice'}>
      <div className="row">
        <span>{problem.message}</span>
        {problem.suggestion && (
          <button type="button" onClick={() => onUse(problem.suggestion!)}>
            Use <span className="mono">{problem.suggestion}</span>
          </button>
        )}
      </div>
    </div>
  )
}

/**
 * Creating a filter: an identifier, a label, a condition. Prompt, cooldown and
 * copy belong to the campaign that will point at it.
 */
function NewFilter({
  existing,
  onCreated,
}: {
  existing: Filter[]
  onCreated: (name: string) => void
}) {
  const [open, setOpen] = useState(false)
  const [name, setName] = useState('')
  const [title, setTitle] = useState('')
  const [when, setWhen] = useState('')
  const [windowDays, setWindowDays] = useState<number | null>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const blocked = slugProblem(name, existing)?.level === 'bad'

  const create = async () => {
    setBusy(true)
    setError(null)
    try {
      await api.createFilter({
        name: name.trim(),
        title: title.trim(),
        when: when.trim() || null,
        window_days: windowDays,
      })
      onCreated(name.trim())
      setName('')
      setTitle('')
      setWhen('')
      setWindowDays(null)
      setOpen(false)
    } catch (err) {
      setError(String((err as Error).message))
    } finally {
      setBusy(false)
    }
  }

  if (!open) {
    return (
      <div className="panel">
        <div className="panel-body compact">
          <div className="row">
            <button onClick={() => setOpen(true)}>+ New filter</button>
            <span className="hint">
              An audience. Nothing mails it until a campaign is pointed at it.
            </span>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="panel">
      <h2>New filter</h2>
      <div className="panel-body">
        {error && <div className="error">{error}</div>}
        {/* TWO FIELDS THAT LOOKED INTERCHANGEABLE. One was "Name — the slug"
            and the other just "Segment", which says nothing about which is
            the permanent machine id and which is the prose people read — and
            the machine id CANNOT BE CHANGED once the filter exists, so
            guessing wrong is not recoverable through this screen. */}
        <div className="row-form">
          <div style={{ minWidth: 260 }}>
            <label htmlFor="new-name">
              Identifier{' '}
              <span className="label-note">
                — permanent, lowercase-with-hyphens, e.g. past-the-free-limit
              </span>
            </label>
            <input
              id="new-name"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              spellCheck={false}
              placeholder="past-the-free-limit"
            />
            <div className="hint caption">
              Shown against every saved list and campaign that uses this filter. <strong>It cannot be changed later</strong> — everything
              else on a filter can.
            </div>
          </div>
          <div style={{ minWidth: 260 }}>
            <label htmlFor="new-title">
              Segment name{' '}
              <span className="label-note">— what people see, e.g. Free, quota pressure</span>
            </label>
            <input
              id="new-title"
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Free, quota pressure"
            />
            <div className="hint caption">
              The label in the table, the pickers and this filter's heading. Editable any time.
            </div>
          </div>
        </div>

        <SlugCheck name={name} existing={existing} onUse={setName} />
        <div className="field">
          <label>
            Condition{' '}
            <span className="label-note">
              — who lands in this segment, built from the names the engine produces
            </span>
          </label>
          <ClauseBuilder
            initial={null}
            initialWindow={windowDays}
            onChange={(next) => setWhen(next ?? '')}
            onWindowChange={setWindowDays}
          />
        </div>
        <div className="row">
          <button
            className="primary"
            // A `bad` problem is one nothing here can fix — the name is taken —
            // so Create is blocked rather than left to fail on the 409. A
            // `warn` never blocks: an untidy slug is a judgement, and refusing
            // to proceed on one would be this screen overruling the operator.
            disabled={busy || !name.trim() || !title.trim() || blocked}
            title={blocked ? 'that identifier is already taken' : undefined}
            onClick={() => void create()}
          >
            {busy ? 'Creating…' : 'Create'}
          </button>
          <button disabled={busy} onClick={() => setOpen(false)}>
            Cancel
          </button>
          <span className="hint">
            Cooldown, caps, copy and the AI instructions are set on the campaign that uses it.
          </span>
        </div>
      </div>
    </div>
  )
}

function FilterDetail({
  filter,
  onChanged,
  onDeleted,
  onRunFilter,
}: {
  filter: Filter
  onChanged: () => void
  onDeleted: () => void
  onRunFilter?: (name: string) => void
}) {
  const [when, setWhen] = useState(filter.when ?? '')
  const [windowDays, setWindowDays] = useState<number | null>(filter.window_days)
  // Reverting remounts the builder rather than pushing a value into it. The
  // builder owns its draft — a half-built row has no expression to be driven
  // by — so "put it back" is "start again from what is stored", which is
  // exactly what a new key does.
  const [builderKey, setBuilderKey] = useState(0)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [saved, setSaved] = useNotice()

  // Saved together, because they are one specification: `visits_in_window >
  // 100` and the width of that window are not independently meaningful, and
  // the API validates the pair.
  const whenChanged =
    (filter.when ?? '') !== when || filter.window_days !== windowDays

  const save = async (body: Record<string, unknown>, what: string) => {
    setBusy(true)
    setError(null)
    setSaved(null)
    try {
      await api.updateFilter(filter.name, body)
      setSaved(`${what} saved`)
      onChanged()
    } catch (err) {
      setError(String((err as Error).message))
    } finally {
      setBusy(false)
    }
  }

  return (
    <div>
      <div className="panel">
        <h2>{filter.title}</h2>
        <div className="panel-body">
          {error && <div className="error">{error}</div>}
          {saved && <div className="notice">{saved}</div>}

          <dl className="facts">
            <dt>Name</dt>
            <dd className="mono">{filter.name}</dd>
            <dt>Campaigns</dt>
            <dd>
              {filter.campaigns ?? 0}
              {filter.enabled_campaigns ? ` (${filter.enabled_campaigns} enabled)` : ''}
              <div className="hint caption">
                Prompt, cooldown, lifetime cap, copy and message class live on each campaign,
                not here. Changing this filter's condition changes who every one of them drafts
                on its next cycle.
              </div>
            </dd>
            <dt>Parked</dt>
            <dd>
              <label className="check">
                <input
                  type="checkbox"
                  style={{ width: 'auto' }}
                  checked={filter.parked}
                  disabled={busy}
                  onChange={(e) => save({ parked: e.target.checked }, 'Parked')}
                />
                {filter.parked ? 'parked — cannot back an enabled campaign' : 'usable'}
              </label>
              <div className="hint caption">
                Parking is how a segment stops being used without deleting the record of it.
              </div>
            </dd>
          </dl>

          {filter.description && <p className="hint">{filter.description}</p>}

          <div className="field">
            <label>
              Condition {filter.unconditional && '(empty = matches everyone; cannot back a campaign)'}
            </label>
            <ClauseBuilder
              key={builderKey}
              initial={filter.when}
              initialWindow={windowDays}
              catchAll={filter.unconditional}
              disabled={busy}
              onChange={(next) => setWhen(next ?? '')}
              onWindowChange={setWindowDays}
            />
          </div>
          <div className="row">
            <button
              className="primary"
              disabled={busy || !whenChanged}
              onClick={() =>
                save(
                  { when: when.trim() || null, window_days: windowDays },
                  'Condition',
                )
              }
            >
              Save condition
            </button>
            {whenChanged && (
              <button
                disabled={busy}
                onClick={() => {
                  setWhen(filter.when ?? '')
                  setWindowDays(filter.window_days)
                  setBuilderKey((n) => n + 1)
                }}
              >
                Revert
              </button>
            )}
            <span className="hint">
              {whenChanged
                ? 'Changing this changes who every campaign on this filter drafts next.'
                : 'Nothing to save — this is the condition as stored.'}
            </span>
          </div>
        </div>
      </div>

      <SampleCount filter={filter} onRunFilter={onRunFilter} />
      <NextStep filter={filter} onRunFilter={onRunFilter} />

      <DangerZone filter={filter} onDeleted={onDeleted} />

      {filter.notes && (
        <div className="panel">
          <h2>Why this filter is the way it is</h2>
          <div className="panel-body">
            <p className="hint">
              Measurement history inherited from v1. Where it talks about the order of groups it
              describes v1's first-match-wins, which v3 does not have (D84); the population sizing
              and the defects it records still hold. Read before changing the condition.
            </p>
            <pre className="notes">{filter.notes}</pre>
          </div>
        </div>
      )}
    </div>
  )
}


/** How many accounts the check reads. See the note below on why it is capped
 *  at all, given that the cap buys no speed. */
const SAMPLE_SCAN = 2000

/**
 * Does this condition select anybody at all?
 *
 * TWO THINGS HERE ARE EASY TO STATE WRONGLY, and both were, in the first cut.
 *
 * THE NUMBER. `POST /runs/preview` with a `limit` reads the first N accounts
 * and evaluates the whole ladder against them, so what comes back is "of 2,000
 * read, M matched". That is not the segment's size and not a share of the base.
 * It is stated with its denominator and never scaled up, because a projection
 * next to a real figure reads as a real figure.
 *
 * THE COST. The cap does NOT make this fast. Measured 2026-09-07 against
 * production: 4m55s at limit 2,000 — the signal query's metric CTEs aggregate
 * over the whole base before a single row is read, which is the same reason the
 * People tab says a scan takes a minute or more regardless of the limit. So the
 * cap buys a bounded, cheaper-to-derive answer, not a quicker one, and the
 * button says minutes rather than implying seconds. Anything else here would be
 * a screen that looks hung.
 *
 * On a saved filter only: preview takes a filter NAME and evaluates every
 * filter above it, so an unsaved draft has nothing to pass — and going through
 * the ladder is the point, since a condition read alone tells you the wrong set.
 */
function SampleCount({
  filter,
  onRunFilter,
}: {
  filter: Filter
  onRunFilter?: (name: string) => void
}) {
  const [result, setResult] = useState<RunPreview | null>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [stopped, setStopped] = useNotice(20000)
  const [cancelKey, setCancelKey] = useState<string | null>(null)
  const [cancelling, setCancelling] = useState(false)

  const count = async () => {
    // The token exists before the request does, because this endpoint is
    // synchronous: it returns when the scan ends, so anything it handed back
    // would arrive too late to cancel with.
    const token = api.cancelToken()
    setBusy(true)
    setCancelKey(token)
    setCancelling(false)
    setError(null)
    setResult(null)
    setStopped(null)
    try {
      setResult(
        await api.previewRun({
          filter: filter.name,
          limit: SAMPLE_SCAN,
          cancel_token: token,
        }),
      )
    } catch (err) {
      // 499 means we asked it to stop. An outcome, not a fault.
      if (err instanceof ApiError && err.status === 499) setStopped(err.detail)
      else setError(String((err as Error).message))
    } finally {
      setBusy(false)
      setCancelKey(null)
    }
  }

  const cancel = async () => {
    if (!cancelKey) return
    setCancelling(true)
    try {
      const res = await api.cancelScan(cancelKey)
      if (!res.stopped) setStopped('It had already finished — nothing to stop.')
    } catch (err) {
      setError(String((err as Error).message))
    } finally {
      setCancelling(false)
    }
  }

  return (
    <div className="panel">
      <h2>Does it select anybody?</h2>
      <div className="panel-body">
        {error && <div className="error">{error}</div>}
        <div className="row">
          <button disabled={busy} onClick={() => void count()}>
            {busy ? (
              <span className="row" style={{ gap: 7 }}>
                <span className="spinner" /> Reading the CRM…
              </span>
            ) : (
              'Check against the CRM — several minutes'
            )}
          </button>
          <span className="hint">
            Reads the first {SAMPLE_SCAN.toLocaleString()} accounts and writes nothing. A
            condition that matches none of them is usually a typo rather than a small segment —
            which is worth five minutes here rather than after a full scan.
          </span>
        </div>

        {stopped && <div className="notice">{stopped}</div>}

        {busy && (
          <div className="notice">
            <div className="row" style={{ gap: 8 }}>
              <span className="spinner" />
              <span>
                Reading up to {SAMPLE_SCAN.toLocaleString()} accounts for{' '}
                <span className="mono">{filter.name}</span>. Nothing will be saved.
              </span>
              <span style={{ flex: 1 }} />
              {cancelKey && (
                <button
                  type="button"
                  className="danger"
                  disabled={cancelling}
                  onClick={() => void cancel()}
                >
                  {cancelling ? 'Stopping…' : 'Cancel'}
                </button>
              )}
            </div>
            <div className="hint caption">
              Measured at roughly two minutes on a filter with no activity window. The cap limits
              the rows read, not the work the query does first — its metric aggregates run over
              the whole base before any row is read, so a smaller cap gives a smaller answer
              rather than a faster one. Leaving this tab does not stop it; <strong>Cancel</strong>{' '}
              does, at the database, freeing the one CRM connection for something else.
            </div>
            <div className="busy-bar" />
          </div>
        )}

        {result && (
          <div className={result.matched ? 'notice' : 'error'}>
            <strong>
              {(result.matched ?? 0).toLocaleString()} of {result.scanned.toLocaleString()} accounts
              scanned
            </strong>{' '}
            landed in <span className="mono">{filter.name}</span>.
            <div className="hint caption">
              The first {SAMPLE_SCAN.toLocaleString()} accounts in the scan's own order, not a
              share of the base — nothing here scales it up to a population. For the real number,
              run it on{' '}
              {onRunFilter ? (
                <button className="linkish" onClick={() => onRunFilter(filter.name)}>
                  stage 2, People
                </button>
              ) : (
                'the People tab'
              )}
              .
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

/**
 * What this filter still needs, and the way on.
 *
 * The stage ends here and nothing said so. Composition NEVER FAILS — every
 * slot ends in an unconditional fallback — so a segment with no approved copy
 * does not error, it produces six sentences that would suit anybody, and the
 * only place that was ever visible was a badge in a table column.
 */
function NextStep({
  filter,
  onRunFilter,
}: {
  filter: Filter
  onRunFilter?: (name: string) => void
}) {
  const own = filter.slots_with_own_copy
  const waiting = filter.awaiting_approval ?? 0

  return (
    <div className="panel">
      <h2>Next: run it</h2>
      <div className="panel-body">
        <ul className="bullets tight">
          <li style={{ fontSize: 12.5 }}>
            {filter.parked ? (
              <>
                <span className="badge parked">parked</span> — this filter still matches, which
                keeps the population visible, but it cannot be run or sent. Untick Parked above
                first.
              </>
            ) : (
              <>
                <span className="badge ok">sendable</span> — nothing about this filter blocks a
                run.
              </>
            )}
          </li>
          <li style={{ fontSize: 12.5 }}>
            {own === 0 ? (
              <>
                <span className="badge bad">no copy</span> — no approved fragment names this
                segment, so every slot falls back to generic copy and a campaign here would send
                six sentences that suit anybody.
                {waiting ? ` ${waiting} fragment(s) are written and awaiting approval.` : ''}
              </>
            ) : own === undefined ? (
              <span className="dim">Copy coverage unknown.</span>
            ) : (
              <>
                <span className={own >= 5 ? 'badge ok' : 'badge parked'}>{own}/6 slots</span> have
                approved copy naming this segment; the rest fall back to generic sentences.
                {waiting ? ` ${waiting} more written and awaiting approval.` : ''}
              </>
            )}
          </li>
        </ul>
        <div className="row" style={{ marginTop: 'var(--s3)' }}>
          {onRunFilter && (
            <button
              className="primary"
              disabled={filter.parked}
              title={filter.parked ? 'a parked filter cannot be run' : undefined}
              onClick={() => onRunFilter(filter.name)}
            >
              Run this filter →
            </button>
          )}
          <span className="hint">
            A run scans the CRM and saves the list of who matched today. A campaign is always
            built from a list that already exists, so the copy and the recipients cannot disagree.
          </span>
        </div>
      </div>
    </div>
  )
}

/**
 * Deleting, and why it usually will not.
 *
 * A filter named by a run or a sendlog row is part of the record of what was
 * sent to whom, so the API refuses. That refusal is worth stating BEFORE the
 * button is pressed rather than after — and parking, which is almost always
 * what somebody actually wants, is offered beside it.
 *
 * Two clicks, because there is no undo.
 */
function DangerZone({ filter, onDeleted }: { filter: Filter; onDeleted: () => void }) {
  const [confirming, setConfirming] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const remove = async () => {
    setBusy(true)
    setError(null)
    try {
      await api.deleteFilter(filter.name)
      onDeleted()
    } catch (err) {
      setError(String((err as Error).message))
      setConfirming(false)
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="panel">
      <h2>Delete</h2>
      <div className="panel-body">
        {error && <div className="error">{error}</div>}
        <p className="hint">
          Refused once a run or a send names this filter — that history has to stay readable, and
          rewriting it is not what anybody means by removing a segment.{' '}
          <strong>Park it instead</strong> to stop it being used while keeping the record.
        </p>
        {confirming ? (
          <div className="row">
            <button className="danger" disabled={busy} onClick={() => void remove()}>
              {busy ? 'Deleting…' : `Yes, delete ${filter.name}`}
            </button>
            <button disabled={busy} onClick={() => setConfirming(false)}>
              Cancel
            </button>
            <span className="hint">
              There is no undo.
            </span>
          </div>
        ) : (
          <button className="danger" onClick={() => setConfirming(true)}>
            Delete this filter
          </button>
        )}
      </div>
    </div>
  )
}
